import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiUtils';
import * as apiUtils from './ChatInterface/apiUtils';
import { debugLog, debugWarn, debugError } from '../utils/debug';

// Protected prompt sets that should never be deleted
const PROTECTED_PROMPT_SETS = [
  'Default Coach',
  'Coach Bridge',
  'Coach Eli',
  'Coach Justin'
];

const PromptSettings = ({ onNavigateBack, activeConversationId }) => {
  const { user } = useUser();
  const [promptSets, setPromptSets] = useState([]);
  // const [currentPromptSet, setCurrentPromptSet] = useState(null); // Removed - not used in current implementation
  const [selectedPromptSetId, setSelectedPromptSetId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPromptSet, setEditingPromptSet] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [nonNegotiableSections, setNonNegotiableSections] = useState(null);
  const [loadingNonNegotiable, setLoadingNonNegotiable] = useState(true);

  // Form state for creating/editing prompt sets
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    basics_prompt: '',
    // New negotiable content fields
    personality_traits: '',
    identity_guidelines: '',
    communication_style: '',
    training_methods: '',
    cultural_context: '',
    coaching_philosophy: ''
  });

  // Function to get token (same pattern as other components)
  const getToken = useCallback(async () => {
    try {
      if (user && user.getToken) {
        const token = await user.getToken();
        return token;
      } 
      if (user && user.id) {
        return user.id;
      }
      throw new Error('No authentication token available');
    } catch (error) {
      debugError('Error getting token:', error);
      throw new Error('Authentication error: ' + error.message);
    }
  }, [user]);

  const loadPromptSets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      const response = await axios.get(
        `${getApiUrl()}/api/prompt-sets`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setPromptSets(response.data);
    } catch (err) {
      debugError('Error loading prompt sets:', err);
      setError('Failed to load prompt sets');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const loadNonNegotiableSections = useCallback(async () => {
    try {
      setLoadingNonNegotiable(true);
      const token = await getToken();
      const response = await axios.get(
        `${getApiUrl()}/api/prompt-sections/non-negotiable`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setNonNegotiableSections(response.data);
    } catch (err) {
      debugError('Error loading non-negotiable sections:', err);
      // Don't set error state - this is not critical, just use fallback text
    } finally {
      setLoadingNonNegotiable(false);
    }
  }, [getToken]);

  // Load prompt sets, non-negotiable sections, and current selection from localStorage
  useEffect(() => {
    if (user) {
      loadPromptSets();
      loadNonNegotiableSections();
      // Load selected prompt set from localStorage
      const saved = localStorage.getItem('selectedPromptSetId');
      if (saved && saved !== 'null') {
        setSelectedPromptSetId(parseInt(saved));
      }
    }
  }, [user, loadPromptSets, loadNonNegotiableSections]);

  const loadPromptSetDetails = async (promptSetId) => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${getApiUrl()}/api/prompt-sets/${promptSetId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      
      return response.data;
    } catch (err) {
      debugError('Error loading prompt set details:', err);
      setError('Failed to load prompt set details');
      return null;
    }
  };

  const handleSelectPromptSet = async (promptSetId) => {
    setSelectedPromptSetId(promptSetId);
    // Save to localStorage for backward compatibility
    localStorage.setItem('selectedPromptSetId', promptSetId?.toString() || '');
    
    // Update the active conversation's coach if we have an active conversation
    if (activeConversationId && promptSetId) {
      try {
        debugLog(`🔄 Switching conversation ${activeConversationId} to coach ${promptSetId}`);
        await apiUtils.switchConversationCoach(user, activeConversationId, promptSetId);
        debugLog('✅ Successfully updated conversation coach');
        
        // Trigger a refresh of conversations data to update the sidebar
        window.dispatchEvent(new Event('runium:refresh-conversations'));
      } catch (err) {
        debugError('Failed to switch conversation coach:', err);
        // Don't show error to user - the localStorage update still works
      }
    }
    
    // Track usage
    if (promptSetId) {
      try {
        const token = await getToken();
        await axios.post(
          `${getApiUrl()}/api/prompt-sets/${promptSetId}/track-usage`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } catch (err) {
        debugWarn('Failed to track usage:', err);
      }
    }
    
    setShowSuccessOverlay(true);
    setTimeout(() => {
      setShowSuccessOverlay(false);
    }, 1500);
  };

  // Function to switch coach for a specific conversation (currently unused)
  // const switchConversationCoach = async (conversationId, promptSetId) => {
  //   try {
  //     const token = await getToken();
  //     const response = await axios.put(
  //       `${getApiUrl()}/messages/conversations/${conversationId}/coach`,
  //       { prompt_set_id: promptSetId },
  //       {
  //         headers: { Authorization: `Bearer ${token}` }
  //       }
  //     );
      
  //     if (response.data.success) {
  //       debugLog(`Successfully switched conversation ${conversationId} to coach: ${response.data.active_prompt_set?.name}`);
  //       return response.data.active_prompt_set;
  //     }
  //   } catch (err) {
  //     debugError('Failed to switch conversation coach:', err);
  //   }
  //   return null;
  // };


  const handleEdit = async (promptSet) => {
    const details = await loadPromptSetDetails(promptSet.id);
    if (details) {
      // If this is the default/original prompt set, create a new one instead of editing
      if (details.is_default) {
        setFormData({
          name: `${details.name} (Custom)`,
          description: details.description || 'Customized version of the default coach prompts',
          basics_prompt: details.basics_prompt,
          personality_traits: details.personality_traits || '',
          identity_guidelines: details.identity_guidelines || '',
          communication_style: details.communication_style || '',
          training_methods: details.training_methods || '',
          cultural_context: details.cultural_context || '',
          coaching_philosophy: details.coaching_philosophy || ''
        });
        setEditingPromptSet(null); // Set to null to create new instead of edit
        setShowEditor(true);
      } else {
        // Normal edit for non-default prompt sets
        setFormData({
          name: details.name,
          description: details.description || '',
          basics_prompt: details.basics_prompt,
          personality_traits: details.personality_traits || '',
          identity_guidelines: details.identity_guidelines || '',
          communication_style: details.communication_style || '',
          training_methods: details.training_methods || '',
          cultural_context: details.cultural_context || '',
          coaching_philosophy: details.coaching_philosophy || ''
        });
        setEditingPromptSet(details);
        setShowEditor(true);
      }
    }
  };

  const handleClone = async (promptSet) => {
    const details = await loadPromptSetDetails(promptSet.id);
    if (details) {
      setFormData({
        name: `${details.name} (Copy)`,
        description: details.description || '',
        basics_prompt: details.basics_prompt,
        personality_traits: details.personality_traits || '',
        identity_guidelines: details.identity_guidelines || '',
        communication_style: details.communication_style || '',
        training_methods: details.training_methods || '',
        cultural_context: details.cultural_context || '',
        coaching_philosophy: details.coaching_philosophy || ''
      });
      setEditingPromptSet(null);
      setShowEditor(true);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const token = await getToken();
      
      if (editingPromptSet) {
        // Update existing
        await axios.put(
          `${getApiUrl()}/api/prompt-sets/${editingPromptSet.id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        // Create new
        await axios.post(
          `${getApiUrl()}/api/prompt-sets`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }
      
      setShowEditor(false);
      await loadPromptSets();
      
      setShowSuccessOverlay(true);
      setTimeout(() => {
        setShowSuccessOverlay(false);
      }, 1500);
      
    } catch (err) {
      debugError('Error saving prompt set:', err);
      setError(err.response?.data?.detail || 'Failed to save prompt set');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promptSetId) => {
    if (!window.confirm('Are you sure you want to delete this prompt set?')) {
      return;
    }
    
    try {
      const token = await getToken();
      await axios.delete(
        `${getApiUrl()}/api/prompt-sets/${promptSetId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // If we were using this prompt set, clear the selection
      if (selectedPromptSetId === promptSetId) {
        setSelectedPromptSetId(null);
        localStorage.removeItem('selectedPromptSetId');
      }
      
      await loadPromptSets();
    } catch (err) {
      debugError('Error deleting prompt set:', err);
      setError(err.response?.data?.detail || 'Failed to delete prompt set');
    }
  };


  // Don't render anything if user is not loaded
  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="relative mb-4 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-blue-400/30 rounded-full"></div>
              <div className="absolute w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-400">Loading prompt settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (showEditor) {
    return (
      <div className="flex flex-col h-full bg-gray-900">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-6 pb-20">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold text-white">
                  {editingPromptSet ? 'Edit Prompt Set' : 'Create Prompt Set from Default'}
                </h1>
                <button
                  onClick={() => setShowEditor(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-400">
                Create your own custom coach prompts based on the Default Coach template. All users can see and use your prompt sets.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4">Basic Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                      placeholder="e.g., Formal Coach, Motivational Trainer"
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                      placeholder="Describe what makes this prompt set unique..."
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Metadata Information (Read-Only) */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">System Information (Read-Only)</h3>
                <p className="text-gray-400 text-sm mb-4">
                  System-level information about this prompt set.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">System Status</label>
                    <div className="bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-gray-400 text-sm">
                      {editingPromptSet?.is_default ? 'Default Coach' : 'Custom Coach'}
                      {editingPromptSet?.is_system_fallback && ' • System Fallback'}
                      {editingPromptSet?.is_deletable ? ' • Deletable' : ' • Protected'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Usage Statistics</label>
                    <div className="bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-gray-400 text-sm">
                      Selected {editingPromptSet?.usage_count || 0} times
                    </div>
                  </div>
                </div>
              </div>

              {/* Non-Negotiable Content (Read-Only) */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  NON-NEGOTIABLE SECTIONS (Read-Only)
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  These core instructions ensure professional coaching standards and cannot be modified.
                </p>
                
                <div className="space-y-3">
                  <div className="border border-gray-600 rounded-lg">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'professional' ? null : 'professional')}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-gray-300 font-medium">Professional Standards</span>
                      <svg 
                        className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'professional' ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedSection === 'professional' && (
                      <div className="px-3 pb-3 text-gray-400 text-sm whitespace-pre-wrap">
                        {loadingNonNegotiable ? (
                          <span className="text-gray-500">Loading...</span>
                        ) : nonNegotiableSections?.professional_standards?.content || (
                          "You are a professional fitness and running coach. Maintain appropriate boundaries and focus on helping users achieve their fitness and running goals. Provide evidence-based advice, be encouraging and supportive, and always prioritize user safety and well-being."
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="border border-gray-600 rounded-lg">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'safety' ? null : 'safety')}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-gray-300 font-medium">Safety Guidelines</span>
                      <svg 
                        className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'safety' ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedSection === 'safety' && (
                      <div className="px-3 pb-3 text-gray-400 text-sm whitespace-pre-wrap">
                        {loadingNonNegotiable ? (
                          <span className="text-gray-500">Loading...</span>
                        ) : nonNegotiableSections?.safety_guidelines?.content || (
                          "Always prioritize user safety and well-being. For any medical concerns, injuries, or health-related questions, guide users to consult with qualified healthcare professionals. Never provide medical diagnoses or treatment advice."
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="border border-gray-600 rounded-lg">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'message-splitting' ? null : 'message-splitting')}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-gray-300 font-medium">Message Splitting Rules</span>
                      <svg 
                        className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'message-splitting' ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedSection === 'message-splitting' && (
                      <div className="px-3 pb-3 text-gray-400 text-sm whitespace-pre-wrap">
                        {loadingNonNegotiable ? (
                          <span className="text-gray-500">Loading...</span>
                        ) : nonNegotiableSections?.message_splitting_rules?.content || (
                          "Split your response into separate messages wherever a human would naturally pause or change topics. This mimics natural conversation and improves user experience."
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="border border-gray-600 rounded-lg">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'client-understanding' ? null : 'client-understanding')}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-700 transition-colors"
                    >
                      <span className="text-gray-300 font-medium">Client Understanding</span>
                      <svg 
                        className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'client-understanding' ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedSection === 'client-understanding' && (
                      <div className="px-3 pb-3 text-gray-400 text-sm whitespace-pre-wrap">
                        {loadingNonNegotiable ? (
                          <span className="text-gray-500">Loading...</span>
                        ) : nonNegotiableSections?.client_understanding?.content || (
                          "Focus on understanding the user's fitness level, running goals, and training needs. Ask about their running experience, current fitness level, any injuries or limitations, and what they want to achieve. Remember important details about their training progress and personal goals."
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Negotiable Content (Editable) */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4">Coach Personality & Style (Editable)</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Customize your coach's personality, communication style, and training approach.
                </p>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Personality Traits</label>
                    <textarea
                      value={formData.personality_traits}
                      onChange={(e) => setFormData(prev => ({...prev, personality_traits: e.target.value}))}
                      placeholder="Describe your coach's personality traits (e.g., encouraging, calm, confident, supportive)..."
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Identity Guidelines</label>
                    <textarea
                      value={formData.identity_guidelines}
                      onChange={(e) => setFormData(prev => ({...prev, identity_guidelines: e.target.value}))}
                      placeholder="How should your coach identify themselves? What makes them unique?..."
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Communication Style</label>
                    <textarea
                      value={formData.communication_style}
                      onChange={(e) => setFormData(prev => ({...prev, communication_style: e.target.value}))}
                      placeholder="How should your coach communicate? Tone, style, use of emojis, phrases...?"
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Training Methods</label>
                    <textarea
                      value={formData.training_methods}
                      onChange={(e) => setFormData(prev => ({...prev, training_methods: e.target.value}))}
                      placeholder="What training philosophy and methods should your coach use? (e.g., Kenyan approach, high volume, tempo training...)"
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Cultural Context</label>
                    <textarea
                      value={formData.cultural_context}
                      onChange={(e) => setFormData(prev => ({...prev, cultural_context: e.target.value}))}
                      placeholder="Any cultural context or references your coach should use? (e.g., Kenyan running culture, local phrases...)"
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Coaching Philosophy</label>
                    <textarea
                      value={formData.coaching_philosophy}
                      onChange={(e) => setFormData(prev => ({...prev, coaching_philosophy: e.target.value}))}
                      placeholder="What's your coach's overall philosophy and approach to training?..."
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>


              {/* Save Button */}
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowEditor(false)}
                  className="px-6 py-2 rounded-lg font-medium border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formData.name}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    saving || !formData.name
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900'
                  }`}
                >
                  {saving ? 'Saving...' : (editingPromptSet ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="flex flex-col h-full bg-gray-900 relative">
      {/* Success Overlay */}
      {showSuccessOverlay && (
        <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Success!</h3>
              <p className="text-gray-400 text-sm">Your prompt settings have been updated</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-20">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">Prompt Settings</h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Customize your coach's personality and behavior by selecting or creating prompt sets.
              Your selection applies to new conversations and is saved permanently when you start chatting.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-500/50 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}


          {/* Actions */}
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-white">Available Prompt Sets</h3>
          </div>

          {/* Prompt Sets List */}
          <div className="space-y-4">
            {promptSets.map((promptSet) => {
              debugLog(`Rendering prompt set: ${promptSet.name} (ID: ${promptSet.id}, is_default: ${promptSet.is_default})`);
              return (
              <div 
                key={promptSet.id} 
                className={`bg-gray-800 rounded-xl p-4 sm:p-6 border transition-colors ${
                  selectedPromptSetId === promptSet.id 
                    ? 'border-blue-500 bg-blue-900/20' 
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <h4 className="text-base sm:text-lg font-semibold text-white">
                        {promptSet.name}
                        {promptSet.is_default && (
                          <span className="ml-2 px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                            Default
                          </span>
                        )}
                      </h4>
                      {selectedPromptSetId === promptSet.id && (
                        <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full self-start">
                          Selected
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-400 text-sm mb-3 leading-relaxed">
                      {promptSet.description || 'No description provided'}
                    </p>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-gray-500">
                      <span>By {promptSet.created_by_name === 'New User' ? 'System' : promptSet.created_by_name}</span>
                      <span className="hidden sm:inline">•</span>
                      <span>Selected {promptSet.usage_count} times</span>
                      <span className="hidden sm:inline">•</span>
                      <span>Created {new Date(promptSet.created_at).toLocaleDateString()}</span>
                      {promptSet.is_system_fallback && (
                        <>
                          <span className="hidden sm:inline">•</span>
                          <span className="text-yellow-400">System Fallback</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 sm:ml-4">
                    {selectedPromptSetId !== promptSet.id && (
                      <button
                        onClick={() => handleSelectPromptSet(promptSet.id)}
                        className="w-full sm:w-auto px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                      >
                        Select
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleEdit(promptSet)}
                      className="w-full sm:w-auto px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded transition-colors"
                      title={promptSet.is_default ? "Create a custom version based on the original" : "Customize this prompt set"}
                    >
                      Customize
                    </button>
                    
                    <button
                      onClick={() => handleClone(promptSet)}
                      className={`w-full sm:w-auto px-3 py-2 text-white text-sm rounded transition-colors ${
                        promptSet.is_default 
                          ? 'bg-green-700 hover:bg-green-600 border border-green-600' 
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                      title={promptSet.is_default ? "Clone the default prompt set to create your own version" : "Clone this prompt set"}
                    >
                      Clone
                    </button>
                    
                    {!promptSet.is_default && !PROTECTED_PROMPT_SETS.includes(promptSet.name) && (
                      <button
                        onClick={() => handleDelete(promptSet.id)}
                        className="w-full sm:w-auto px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          
          {promptSets.length === 0 && (
            <div className="text-center py-8 sm:py-12">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">No Prompt Sets Found</h3>
              <p className="text-sm sm:text-base text-gray-400 mb-4">Clone the Default Coach to create your first custom prompt set.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptSettings; 